import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { UploadCloud, X, Loader, CheckCircle, AlertCircle, Eye } from 'lucide-react';

const FloorPlanUpload = ({ isOpen, onClose, onProcessingComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError(null);
      setResult(null);
      setLogs([]);
    }
  };

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError(null);
      setResult(null);
      setLogs([]);
    }
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) return;

    setProcessing(true);
    setError(null);
    setResult(null);
    setLogs([]);

    addLog('Upload wird gestartet...', 'info');

    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      addLog('Bild wird an den OCR-Dienst gesendet...', 'info');
      const response = await axios.post('https://api.clara360.de/floorplan/analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          addLog(`Upload-Fortschritt: ${percentCompleted}%`, 'info');
        },
      });

      addLog('Antwort vom Server erhalten', 'success');
      setResult(response.data);
      onProcessingComplete(response.data);
      addLog('Verarbeitung abgeschlossen', 'success');

    } catch (err) {
      addLog('Fehler bei der Verarbeitung', 'error');
      setError(err.response ? err.response.data.error : err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Grundriss-Digitalisierung (Clara OCR CV Hybrid)</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-grow grid grid-cols-2 gap-6 p-6 overflow-y-auto">
          {/* Left Side: Upload and Preview */}
          <div className="flex flex-col space-y-4">
            <div 
              className="flex-grow border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-center p-6 relative"
            >
              {preview ? (
                <>
                  <img src={preview} alt="Vorschau" className="max-h-full max-w-full object-contain rounded-md" />
                  <button 
                    onClick={() => { setSelectedFile(null); setPreview(null); }}
                    className="absolute top-2 right-2 p-1.5 bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full shadow-md"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center space-y-3 text-gray-500">
                  <UploadCloud className="w-12 h-12 text-gray-400" />
                  <p className="font-medium">Grundriss hierher ziehen oder klicken, um hochzuladen</p>
                  <p className="text-sm">PNG, JPG, oder GIF (max. 10MB)</p>
                  <input 
                    type="file" 
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={handleFileChange}
                    accept="image/png, image/jpeg, image/gif"
                  />
                </div>
              )}
            </div>
            <div className="flex-shrink-0">
              <button 
                onClick={handleUpload} 
                disabled={!selectedFile || processing}
                className="w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {processing ? (
                  <><Loader className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" /> Verarbeitung...</>
                ) : (
                  'Digitalisierung starten'
                )}
              </button>
            </div>
          </div>

          {/* Right Side: Logs and Results */}
          <div className="flex flex-col space-y-6">
            <div className="flex-grow bg-gray-50 rounded-lg p-4 border overflow-y-auto">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Status & Ergebnisse</h3>
              {processing && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <Loader className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Grundriss wird analysiert...</p>
                    <p className="text-sm text-gray-500 mt-1">Dies kann einen Moment dauern.</p>
                    {logs.length > 0 && (
                      <div className="text-left mt-4 text-xs text-gray-500 bg-white p-2 rounded-md border max-h-32 overflow-y-auto">
                        {logs.map((log, index) => (
                          <p key={index} className="truncate">{log.message}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {error && (
                <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              {result && (
                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <h4 className="font-medium text-green-900">Analyse erfolgreich!</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-green-700 font-medium">Räume erkannt:</span>
                      <span className="ml-2 text-green-900">{result.rooms_detected}</span>
                    </div>
                    <div>
                      <span className="text-green-700 font-medium">Texte erkannt:</span>
                      <span className="ml-2 text-green-900">{result.total_detections}</span>
                    </div>
                    <div>
                      <span className="text-green-700 font-medium">Benannte Räume:</span>
                      <span className="ml-2 text-green-900">{result.rooms_with_names}</span>
                    </div>
                    <div>
                      <span className="text-green-700 font-medium">Verarbeitung:</span>
                      <span className="ml-2 text-green-900">CV + OCR Hybrid</span>
                    </div>
                  </div>
                  {result.room_polygons && result.room_polygons.length > 0 && (
                    <div className="mt-4">
                      <h5 className="font-medium text-green-900 mb-2">Erkannte Räume:</h5>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {result.room_polygons.slice(0, 10).map((room, index) => (
                          <div key={index} className="text-xs text-green-800">
                            <span className="font-medium">Raum {index + 1}:</span>
                            <span className="ml-1">
                              {room.room_name || 'Unbenannt'}
                              {room.area_text && ` (${room.area_text})`}
                            </span>
                          </div>
                        ))}
                        {result.room_polygons.length > 10 && (
                          <div className="text-xs text-green-700 italic">
                            ... und {result.room_polygons.length - 10} weitere Räume
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!processing && !result && !error && (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center h-full flex flex-col justify-center">
                  <Eye className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    Laden Sie einen Grundriss hoch und starten Sie die Digitalisierung
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Clara OCR CV Hybrid erkennt Räume und Texte automatisch
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorPlanUpload;

